import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SelectorField from "../../common/Components/SearchBarField/SelectorField";
import InputSearch from "../../common/Components/SearchBarField/InputSearch";
import { Button } from "reactstrap";
import StudioFilterBar from "../../common/Components/FilterBar/StudioFilterBar";

const useCompactFilterBar = () => {
  const [compact, setCompact] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const onChange = () => setCompact(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  return compact;
};

const STOCK_OPTIONS = [
  { label: "All stock", value: "" },
  { label: "In stock", value: "true" },
  { label: "Out of stock", value: "false" },
];

const hasFilterValue = (value: any) => {
  if (value == null || value === "") {
    return false;
  }
  if (typeof value === "object") {
    return Boolean(value.value);
  }
  return true;
};

const FilterField = ({
  label,
  className = "",
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={`qf-field${className ? ` ${className}` : ""}`}>
    <span className="qf-field-label">{label}</span>
    {children}
  </div>
);

const toDateInput = (value: any) => {
  if (!value) {
    return "";
  }
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    return text.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().slice(0, 10);
};

const DateInput = (props: any) => (
  <div className="InputColP">
    <div className="InputDiv">
      <input
        type="date"
        name={props.name}
        value={toDateInput(props.value)}
        onChange={props.onChange}
        onKeyDown={props.onKeyDown}
      />
    </div>
  </div>
);

const ExtraProductFilters = (props: any) => {
  const { filters } = props;
  return (
    <>
      {props.additionalFilters}
      <section className="qf-more-section">
        <div className="qf-more-section-head">
          <p className="qf-more-section-title">Search</p>
          <label className="qf-ai-toggle">
            <input
              type="checkbox"
              checked={filters?.includeAiSuggestions !== false}
              onChange={(e: any) =>
                props.handleOnchangeFilter({
                  name: "includeAiSuggestions",
                  value: e.target.checked,
                })
              }
            />
            Include AI guesses
          </label>
        </div>
        <div className="qf-more-grid">
          <FilterField label="Description">
            <div className="InputColP">
              <InputSearch
                {...props}
                name="q"
                placeholder="e.g. black saree after 2025 in stock"
                value={filters?.q || ""}
                onChange={(e: any) =>
                  props.handleOnchangeFilter({
                    name: "q",
                    value: e.target.value,
                  })
                }
                onKeyDown={(event: any) => props.handleKeyDown(event)}
              />
            </div>
          </FilterField>
          <FilterField label="Color">
            <div className="InputColP">
              <InputSearch
                {...props}
                name="color"
                placeholder="Any color"
                value={filters?.color || ""}
                onChange={(e: any) =>
                  props.handleOnchangeFilter({
                    name: "color",
                    value: e.target.value,
                  })
                }
                onKeyDown={(event: any) => props.handleKeyDown(event)}
              />
            </div>
          </FilterField>
          <FilterField label="From">
            <DateInput
              name="createdAfter"
              value={filters?.createdAfter}
              onChange={(e: any) =>
                props.handleOnchangeFilter({
                  name: "createdAfter",
                  value: e.target.value,
                })
              }
              onKeyDown={(event: any) => props.handleKeyDown(event)}
            />
          </FilterField>
          <FilterField label="To">
            <DateInput
              name="createdBefore"
              value={filters?.createdBefore}
              onChange={(e: any) =>
                props.handleOnchangeFilter({
                  name: "createdBefore",
                  value: e.target.value,
                })
              }
              onKeyDown={(event: any) => props.handleKeyDown(event)}
            />
          </FilterField>
          <FilterField label="Stock">
            <div className="InputColP">
              <SelectorField
                name="inStock"
                placeholder="All stock"
                options={STOCK_OPTIONS}
                value={filters?.inStock || ""}
                onChange={(e: any) =>
                  props.handleOnchangeFilter({
                    name: "inStock",
                    value: e,
                  })
                }
              />
            </div>
          </FilterField>
        </div>
      </section>

      <section className="qf-more-section">
        <p className="qf-more-section-title">Classification</p>
        <div className="qf-more-grid">
          <FilterField label="Artform">
            <div className="InputColP">
              <SelectorField
                {...props}
                name="artform"
                placeholder="Select artform"
                options={props.artformOptions}
                onChange={(e: any) =>
                  props.handleOnchangeFilter({
                    name: "artform",
                    value: e,
                  })
                }
                value={filters?.artform || ""}
                onInputChange={(e: any) =>
                  props.handleOnInputChange({
                    name: "artform",
                    value: e,
                  })
                }
                onFocus={() => {
                  props.autoFocus({
                    name: "artform",
                  });
                }}
              />
            </div>
          </FilterField>
          <FilterField label="Type">
            <div className="InputColP">
              <SelectorField
                {...props}
                name="type"
                placeholder="Select type"
                options={
                  filters?.artform
                    ? props.typeOptions
                    : [
                        {
                          label: (
                            <p className="req">***Please select artform***</p>
                          ),
                          value: "",
                        },
                      ]
                }
                onChange={(e: any) =>
                  props.handleOnchangeFilter({
                    name: "type",
                    value: e,
                  })
                }
                value={filters?.type || ""}
                onFocus={() => {
                  if (filters?.artform) {
                    props.autoFocus({
                      name: "type",
                    });
                  }
                }}
                onInputChange={(e: any) =>
                  props.handleOnInputChange({
                    name: "type",
                    value: e,
                  })
                }
              />
            </div>
          </FilterField>
          <FilterField label="Category">
            <div className="InputColP">
              <SelectorField
                {...props}
                placeholder="Select category"
                name="category"
                onChange={(e: any) =>
                  props.handleOnchangeFilter({
                    name: "category",
                    value: e,
                  })
                }
                value={filters?.category || ""}
                options={props.categoryOptions}
                onFocus={() => {
                  props.autoFocus({
                    name: "category",
                  });
                }}
                onInputChange={(e: any) =>
                  props.handleOnInputChange({
                    name: "category",
                    value: e,
                  })
                }
              />
            </div>
          </FilterField>
          <FilterField label="Sub-category">
            <div className="InputColP">
              <SelectorField
                {...props}
                name="subCategory"
                placeholder="Select sub-category"
                value={filters?.subCategory || ""}
                options={
                  filters?.category && !filters.category.byName
                    ? props.subcategoryOptions
                    : [
                        {
                          label: (
                            <p className="req">
                              {filters?.category
                                ? "***Pick a type, then its category, to filter sub-category***"
                                : "***Please select category***"}
                            </p>
                          ),
                          value: "",
                        },
                      ]
                }
                onChange={(e: any) =>
                  props.handleOnchangeFilter({
                    name: "subCategory",
                    value: e,
                  })
                }
                onFocus={() => {
                  if (filters?.category && !filters.category.byName) {
                    props.autoFocus({
                      name: "subCategory",
                    });
                  }
                }}
                onInputChange={(e: any) =>
                  props.handleOnInputChange({
                    name: "subCategory",
                    value: e,
                  })
                }
              />
            </div>
          </FilterField>
          <FilterField label="Weave type">
            <div className="InputColP">
              <SelectorField
                {...props}
                name="weaveType"
                id="weaveType"
                placeholder="Select weave type"
                value={filters?.weaveType || ""}
                options={
                  filters?.subCategory?.value
                    ? props?.weaveTypeOptions
                    : [
                        {
                          label: (
                            <p className="req">
                              ***Please select sub-category***
                            </p>
                          ),
                          value: "",
                        },
                      ]
                }
                onChange={(e: any) =>
                  props.handleOnchangeFilter({
                    name: "weaveType",
                    value: e,
                  })
                }
                onFocus={() => {
                  if (props?.filters?.subCategory?.value) {
                    props.autoFocus({
                      name: "weaveType",
                    });
                  }
                }}
                onInputChange={(e: any) =>
                  props.handleOnInputChange({
                    name: "weaveType",
                    value: e,
                  })
                }
              />
            </div>
          </FilterField>
        </div>
      </section>
    </>
  );
};

const QuickFilter = (props: any) => {
  const { filtersData }: any = props;
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement | null>(null);
  let filetrsForProduct: any = localStorage.getItem("FiltersForProducts");
  let filters: any = filtersData
    ? filtersData
    : JSON.parse(filetrsForProduct) || "";
  const extraActiveCount = [
    filters?.price?.preset && filters.price.preset !== "ALL" ? filters.price.preset : "",
    filters?.artform,
    filters?.type,
    filters?.category,
    filters?.subCategory,
    filters?.weaveType,
    filters?.createdAfter,
    filters?.createdBefore,
    filters?.inStock?.value ? filters.inStock : "",
  ].filter(hasFilterValue).length;
  const extraFilterProps = { ...props, filters };
  const compactBar = useCompactFilterBar();
  const trailingActions = props.trailingActions;

  useEffect(() => {
    if (!moreOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (moreRef.current && moreRef.current.contains(target)) {
        return;
      }
      if (target.closest(".rs-picker-popup, .rs-picker-daterange-menu, .rs-anim-fade, .react-select__menu")) {
        return;
      }
      setMoreOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [moreOpen]);

  const compactFields = [
    {
      id: "productName",
      label: "Product",
      placeholder: "Search product name",
      value: filters?.productName || "",
      onChange: (value: string) =>
        props.handleOnchangeFilter({ name: "productName", value }),
    },
    {
      id: "xuid",
      label: "HKP tag",
      placeholder: "Search HKP tag",
      value: filters?.xuid || "",
      onChange: (value: string) =>
        props.handleOnchangeFilter({ name: "xuid", value }),
    },
    {
      id: "ProductId",
      label: "Product ID",
      placeholder: "Search product ID",
      value: filters?.ProductId || "",
      onChange: (value: string) =>
        props.handleOnchangeFilter({ name: "ProductId", value }),
    },
    {
      id: "tags",
      label: "Tag",
      placeholder: "Search tag",
      value: filters?.tags || "",
      onChange: (value: string) =>
        props.handleOnchangeFilter({ name: "tags", value }),
    },
    {
      id: "color",
      label: "Colour",
      placeholder: "Search colour",
      value: filters?.color || "",
      onChange: (value: string) =>
        props.handleOnchangeFilter({ name: "color", value }),
    },
    {
      id: "q",
      label: "Description",
      placeholder: "Search description",
      value: filters?.q || "",
      onChange: (value: string) =>
        props.handleOnchangeFilter({ name: "q", value }),
    },
  ];

  return (
    <StudioFilterBar
      fields={compactFields}
      defaultFieldId="productName"
      onKeyDown={(event) => props.handleKeyDown(event)}
      onReset={() => {
        setMoreOpen(false);
        props.handleResetData();
      }}
      addTo={props.hideCreateProduct ? undefined : "/createProduct"}
      addAriaLabel="Add product"
      addLabel="Add product"
      hideAdd={props.hideCreateProduct}
      moreContent={<ExtraProductFilters {...extraFilterProps} />}
      moreCount={extraActiveCount}
      trailing={compactBar ? trailingActions : undefined}
      wideToolbar={
    <div className="qf-filter-stack" ref={moreRef}>
      <div
        className={
          "QuickFiltersHeader1 QuickFiltersHeader1--compact" +
          (props.hideCreateProduct ? "" : " ps-3 pe-3")
        }
      >
        {!props.hideCreateProduct ? (
          <div className="InputColP2">
            <div>
              <Link to="/createProduct">
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 34 34"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="34" height="34" rx="4" fill="#1A1A1A" />
                  <path
                    d="M17.5 12.5503V22.4497"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12.5503 17.5H22.4498"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>
            <div>
              <Button
                className="ResetBtn"
                onClick={() => {
                  setMoreOpen(false);
                  props.handleResetData();
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        ) : null}
        {!props.hideCreateProduct ? (
          <div className="InputColP addIcon">
            <Link to="/createProduct">
              <svg
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="34" height="34" rx="4" fill="#1A1A1A" />
                <path
                  d="M17.5 12.5503V22.4497"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.5503 17.5H22.4498"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        ) : null}
        <div className="InputColP">
          <InputSearch
            {...props}
            name="xuid"
            placeholder="HKP Tag"
            value={filters?.xuid || ""}
            onChange={(e: any) =>
              props.handleOnchangeFilter({
                name: "xuid",
                value: e.target.value,
              })
            }
            onKeyDown={(event: any) => props.handleKeyDown(event)}
          />
        </div>
        <div className="InputColP">
          <InputSearch
            {...props}
            name="ProductId"
            placeholder="Product ID"
            value={filters?.ProductId || ""}
            onChange={(e: any) =>
              props.handleOnchangeFilter({
                name: "ProductId",
                value: e.target.value,
              })
            }
            onKeyDown={(event: any) => props.handleKeyDown(event)}
          />
        </div>
        <div className="InputColP">
          <InputSearch
            {...props}
            name="tags"
            placeholder="Tag"
            value={filters?.tags || ""}
            onChange={(e: any) =>
              props.handleOnchangeFilter({
                name: "tags",
                value: e.target.value,
              })
            }
            onKeyDown={(event: any) => props.handleKeyDown(event)}
          />
        </div>
        <div className="InputColP">
          <InputSearch
            {...props}
            name="product"
            placeholder="Product"
            value={filters?.productName || ""}
            onChange={(e: any) =>
              props.handleOnchangeFilter({
                name: "productName",
                value: e.target.value,
              })
            }
            onKeyDown={(event: any) => props.handleKeyDown(event)}
          />
        </div>
        <div className="qf-more-wrap">
          <Button
            type="button"
            className={`qf-more-btn${moreOpen ? " is-open" : ""}`}
            onClick={() => setMoreOpen((open) => !open)}
          >
            More filters{extraActiveCount ? ` (${extraActiveCount})` : ""}
          </Button>
        </div>
        <div className="InputColP1 searchIcon">
          <Button
            className="ResetBtn"
            onClick={() => {
              setMoreOpen(false);
              props.handleResetData();
            }}
          >
            Reset
          </Button>
        </div>
        {!compactBar && trailingActions ? (
          <div className="product-list-toolbar-extra">{trailingActions}</div>
        ) : null}
      </div>
      {moreOpen ? (
        <div className="qf-more-panel">
          <ExtraProductFilters {...extraFilterProps} />
        </div>
      ) : null}
    </div>
      }
    />
  );
};

export default QuickFilter;
